import { useForm } from '@tanstack/react-form'
import { capitalize } from 'lodash'
import { FilterIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useExpensesStore } from '../store/expensesStore'
import type { ExpensesFilter } from '@/schemas/filterSchemas'
import type { ExpensesStatus } from '../types/expenses'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Badge } from '@/components/custom/badge'
import DatePicker from '@/components/custom/date-picker'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useIsMobile } from '@/hooks'
import IconInput from '@/components/custom/icon-input'
import { parseLocalDateString, toLocalDateString } from '@/utils/timeZoneUtils'

export default function ExpensesFilter({
  onFilter,
  onCloseDrawer,
  initialFilters,
}: {
  onFilter?: (filters: ExpensesFilter) => void
  onCloseDrawer?: () => void
  initialFilters: ExpensesFilter
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  const churches = useChurchesStore((s) => s.churches)
  const categories = useExpensesStore((s) => s.categories)

  const statusOptions: Array<ExpensesStatus> = [
    'pending',
    'approved',
    'updating',
    'cancelled',
  ]

  const form = useForm({
    defaultValues: {
      search: initialFilters.search,
      categoryId: initialFilters.categoryId,
      churchId: initialFilters.churchId,
      startDate: initialFilters.startDate,
      endDate: initialFilters.endDate,
      status: initialFilters.status,
    },
    onSubmit: async ({ value }) => {
      const startDateStr = value.startDate
        ? typeof value.startDate === 'string'
          ? value.startDate
          : toLocalDateString(value.startDate)
        : ''

      const endDateStr = value.endDate
        ? typeof value.endDate === 'string'
          ? value.endDate
          : toLocalDateString(value.endDate)
        : ''

      onFilter?.({
        ...value,
        startDate: startDateStr,
        endDate: endDateStr,
      })

      await new Promise((resolve) => setTimeout(resolve, 0))
      setOpen(false)
      if (isMobile && onCloseDrawer) onCloseDrawer()
    },
  })

  useEffect(() => {
    form.setFieldValue('churchId', initialFilters.churchId)
    form.setFieldValue('status', initialFilters.status)
    form.setFieldValue('startDate', initialFilters.startDate)
    form.setFieldValue('endDate', initialFilters.endDate)
  }, [initialFilters])

  const handleClear = async () => {
    form.reset()

    onFilter?.({
      churchId: [],
      categoryId: [],
      startDate: '',
      endDate: '',
      status: '',
      search: isMobile ? '' : initialFilters.search,
    })
    await new Promise((resolve) => setTimeout(resolve, 0))
    setOpen(false)
  }

  const filtersActive =
    form.state.values.churchId.length > 0 ||
    form.state.values.categoryId.length > 0 ||
    form.state.values.status !== '' ||
    form.state.values.startDate !== '' ||
    form.state.values.endDate !== ''
  const activeCount = [
    form.state.values.churchId.length > 0,
    form.state.values.categoryId.length > 0,
    form.state.values.status !== '',
    form.state.values.startDate !== '',
    form.state.values.endDate !== '',
  ].filter(Boolean).length

  const filterForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {isMobile && (
        <form.Field
          name="search"
          children={(field) => (
            <IconInput
              placeholder="Search expenses"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              className="w-full"
            />
          )}
        />
      )}
      <form.Field
        name="churchId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Churches"
            id={field.name}
            name={field.name}
            options={churches.map((c) => ({
              value: c._id,
              label: c.name,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="categoryId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Categories"
            id={field.name}
            name={field.name}
            options={categories.map((c) => ({
              value: c._id,
              label: c.name,
            }))}
            multiple
          />
        )}
      />
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="startDate"
          children={(field) => (
            <DatePicker
              field={{
                ...field,
                state: {
                  ...field.state,
                  value: field.state.value
                    ? typeof field.state.value === 'string'
                      ? parseLocalDateString(field.state.value)
                      : field.state.value
                    : undefined,
                },
                handleChange: (date: Date | undefined) => {
                  const stringDate = date ? toLocalDateString(date) : ''
                  field.handleChange(stringDate)
                },
              }}
              label="Start Date"
              startYear={2000}
              disableFutureDates
              id={field.name}
              name={field.name}
              showClearButton
            />
          )}
        />
        <form.Subscribe
          selector={(state) => [state.values.startDate]}
          children={([startDateValue]) => (
            <form.Field
              name="endDate"
              children={(field) => (
                <DatePicker
                  field={{
                    ...field,
                    state: {
                      ...field.state,
                      value: field.state.value
                        ? typeof field.state.value === 'string'
                          ? parseLocalDateString(field.state.value)
                          : field.state.value
                        : undefined,
                    },
                    handleChange: (date: Date | undefined) => {
                      const stringDate = date ? toLocalDateString(date) : ''
                      field.handleChange(stringDate)
                    },
                  }}
                  label="End Date"
                  startYear={2000}
                  disableFutureDates
                  minDate={
                    startDateValue
                      ? parseLocalDateString(startDateValue)
                      : undefined
                  }
                  id={field.name}
                  name={field.name}
                  showClearButton
                />
              )}
            />
          )}
        />
      </div>

      <form.Field
        name="status"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Expense Status"
            id={field.name}
            name={field.name}
            options={statusOptions.map((s) => ({
              value: s,
              label: capitalize(s.toLowerCase()),
            }))}
            clearable
          />
        )}
      />

      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.values.churchId,
          state.values.categoryId,
          state.values.startDate,
          state.values.endDate,
          state.values.status,
          state.values.search,
        ]}
        children={([
          canSubmit,
          churchIdValue,
          categoryIdValue,
          startDateValue,
          endDateValue,
          statusValue,
          searchValue,
        ]) => {
          const hasFilter =
            (Array.isArray(churchIdValue) && churchIdValue.length) ||
            (Array.isArray(categoryIdValue) && categoryIdValue.length) ||
            statusValue !== '' ||
            startDateValue ||
            endDateValue
          const hasMobileSearch = isMobile && searchValue
          return (
            <div
              className={
                !isMobile ? 'flex gap-2 justify-end pt-2' : 'space-y-2'
              }
            >
              <Button type="button" variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || (!hasFilter && !hasMobileSearch)}
              >
                Apply
              </Button>
            </div>
          )
        }}
      />
    </form>
  )

  if (isMobile) {
    return <div className="w-full">{filterForm}</div>
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={filtersActive ? 'relative' : ''}>
          <FilterIcon size={16} />
          Filters
          {filtersActive && <Badge color="red">{activeCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-4 min-w-[320px] max-w-100">
        {filterForm}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
